/**
 * Activities Service — Quiz/Survey extension API client.
 *
 * Calls cdb_tqs endpoints under /api/v1/trainings/{id}/... via the BFF proxy.
 * Mirrors app/api/routes/activities.py.
 */

import { requireAccessToken } from '@/services/auth-service'
import { getTqsProxyBase } from '@/lib/tqs-proxy'
import { withTrainingClientScope } from '@/lib/training-client-scope'
import { apiErrorMessage } from '@/lib/api-error'
import type {
  Activity,
  ActivityAnalysis,
  ActivityAnalysisCreateResponse,
  ActivityFields,
  ActivityQuestion,
  ActivityQuestionCreate,
  ActivityQuestionUpdate,
  ActivityStatistics,
  ActivitySubmission,
  ActivitySubmissionCreate,
  ActivitySubmissionListResponse,
  MarkdownImportJob,
  MarkdownImportJobCreated,
  MarkdownImportResponse,
  Invitation,
  InvitationCreate,
  QuestionnaireCreate,
  QuestionnaireRow,
  SuggestQuestionsRequest,
  SuggestQuestionsResponse,
  QuestionnaireVersionInfo,
} from '@/types/activities'

/** Every call names the client an admin picked in the Training Hub (cdb_tqs#1574). */
function scopedFetch(url: string, init?: RequestInit): Promise<Response> {
  return fetch(withTrainingClientScope(url), init)
}

async function authedJson<T>(
  path: string,
  init?: RequestInit & { signal?: AbortSignal },
): Promise<T> {
  const token = await requireAccessToken()
  const url = `${getTqsProxyBase()}${path}`
  const response = await scopedFetch(url, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(init?.headers || {}),
    },
  })
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }))
    throw new Error(apiErrorMessage(error, `Request failed (${response.status})`))
  }
  // Handle 204 / empty bodies
  if (response.status === 204) return undefined as unknown as T
  return response.json()
}

export const activitiesService = {
  // ==========================================================================
  // QUESTIONS
  // ==========================================================================

  async listQuestions(trainingId: string): Promise<ActivityQuestion[]> {
    return authedJson<ActivityQuestion[]>(`/trainings/${trainingId}/questions`)
  },

  async createQuestion(
    trainingId: string,
    payload: ActivityQuestionCreate,
  ): Promise<ActivityQuestion> {
    return authedJson<ActivityQuestion>(`/trainings/${trainingId}/questions`, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },

  async updateQuestion(
    trainingId: string,
    questionId: string,
    payload: ActivityQuestionUpdate,
  ): Promise<ActivityQuestion> {
    return authedJson<ActivityQuestion>(
      `/trainings/${trainingId}/questions/${questionId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(payload),
      },
    )
  },

  async deleteQuestion(trainingId: string, questionId: string): Promise<void> {
    await authedJson<void>(`/trainings/${trainingId}/questions/${questionId}`, {
      method: 'DELETE',
    })
  },

  /** Rewrite every question's position to its index in `orderedIds`. The
   *  backend does this in a single transaction — the UNIQUE constraint on
   *  (training_id, position) makes per-row PATCHes from the client unsafe. */
  async reorderQuestions(
    trainingId: string,
    orderedIds: string[],
  ): Promise<ActivityQuestion[]> {
    return authedJson<ActivityQuestion[]>(
      `/trainings/${trainingId}/questions/reorder`,
      {
        method: 'POST',
        body: JSON.stringify(orderedIds),
      },
    )
  },

  // ==========================================================================
  // ACTIVITY METADATA + LIFECYCLE
  // ==========================================================================

  async getActivity(trainingId: string): Promise<Activity> {
    return authedJson<Activity>(`/trainings/${trainingId}/activity`)
  },

  /** List trainings that reference this activity as their attached_quiz_id. */
  async listAttachers(trainingId: string): Promise<{ id: string; title: string }[]> {
    return authedJson<{ id: string; title: string }[]>(
      `/trainings/${trainingId}/attachers`,
    )
  },

  async updateActivity(
    trainingId: string,
    fields: ActivityFields,
  ): Promise<Activity> {
    return authedJson<Activity>(`/trainings/${trainingId}/activity`, {
      method: 'PATCH',
      body: JSON.stringify(fields),
    })
  },

  /** The PATCH body carries every field, so a single-field change reads the activity first. */
  async patchActivity(trainingId: string, patch: Partial<ActivityFields>): Promise<Activity> {
    const a = await this.getActivity(trainingId)
    return this.updateActivity(trainingId, {
      analysis_mode: a.analysis_mode,
      audience_type: a.audience_type,
      knowledge_area: a.knowledge_area,
      passing_score: a.passing_score,
      collect_respondent_info: a.collect_respondent_info,
      deadline_date: a.deadline_date,
      anonymous: a.anonymous,
      show_result: a.show_result ?? true,
      show_correct_answers: a.show_correct_answers ?? false,
      shuffle_questions: a.shuffle_questions ?? false,
      ...patch,
    })
  },

  async activate(trainingId: string): Promise<Activity> {
    return authedJson<Activity>(`/trainings/${trainingId}/activate`, {
      method: 'POST',
    })
  },

  async close(trainingId: string): Promise<Activity> {
    return authedJson<Activity>(`/trainings/${trainingId}/close`, {
      method: 'POST',
    })
  },

  /** Manually move an activity back to draft. Backend rejects with 409 if
   *  there are still trainings attaching this activity as their quiz. */
  async resetToDraft(trainingId: string): Promise<Activity> {
    return authedJson<Activity>(`/trainings/${trainingId}/reset-to-draft`, {
      method: 'POST',
    })
  },

  // ==========================================================================
  // UMFRAGEN & QUIZ HUB (library, invitations, assistant)
  // ==========================================================================

  /** Every questionnaire of the client with counts and distribution — one request. */
  async listQuestionnaires(clientId?: string | null): Promise<QuestionnaireRow[]> {
    const query = clientId ? `?client_id=${encodeURIComponent(clientId)}` : ''
    const data = await authedJson<{ questionnaires: QuestionnaireRow[]; total: number }>(
      `/trainings/questionnaires${query}`,
    )
    return data.questionnaires
  },

  /** A quiz or a survey by kind and title; the kind is fixed from here on. */
  async createQuestionnaire(data: QuestionnaireCreate): Promise<Activity> {
    return authedJson<Activity>('/trainings/questionnaires', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async listInvitations(trainingId: string): Promise<Invitation[]> {
    return authedJson<Invitation[]>(`/trainings/${trainingId}/invitations`)
  },

  async createInvitation(trainingId: string, data: InvitationCreate): Promise<Invitation> {
    return authedJson<Invitation>(`/trainings/${trainingId}/invitations`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async revokeInvitation(trainingId: string, invitationId: string): Promise<void> {
    await authedJson<void>(`/trainings/${trainingId}/invitations/${invitationId}`, { method: 'DELETE' })
  },

  /** Question proposals from the assistant (intermediate tier); nothing is saved. */
  async suggestQuestions(trainingId: string, data: SuggestQuestionsRequest): Promise<SuggestQuestionsResponse> {
    return authedJson<SuggestQuestionsResponse>(`/trainings/${trainingId}/questions/suggest`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  // ==========================================================================
  // SUBMISSIONS
  // ==========================================================================

  async submit(
    trainingId: string,
    payload: ActivitySubmissionCreate,
  ): Promise<ActivitySubmission> {
    return authedJson<ActivitySubmission>(`/trainings/${trainingId}/submit`, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },

  /** Withdraw the calling user's own submission so they can re-take the
   *  quiz. Backend allows this only for activity creators and admins —
   *  regular respondents get 403. */
  async withdrawMySubmission(trainingId: string): Promise<void> {
    await authedJson<void>(`/trainings/${trainingId}/my-submission`, {
      method: 'DELETE',
    })
  },

  async listSubmissions(trainingId: string): Promise<ActivitySubmissionListResponse> {
    return authedJson<ActivitySubmissionListResponse>(
      `/trainings/${trainingId}/submissions`,
    )
  },

  async getSubmission(
    trainingId: string,
    submissionId: string,
  ): Promise<ActivitySubmission> {
    return authedJson<ActivitySubmission>(
      `/trainings/${trainingId}/submissions/${submissionId}`,
    )
  },

  // ==========================================================================
  // STATISTICS + ANALYSIS
  // ==========================================================================

  async getStatistics(trainingId: string, versionId?: string | null): Promise<ActivityStatistics> {
    const query = versionId ? `?version_id=${encodeURIComponent(versionId)}` : ''
    return authedJson<ActivityStatistics>(`/trainings/${trainingId}/statistics${query}`)
  },

  async listVersions(trainingId: string): Promise<QuestionnaireVersionInfo[]> {
    return authedJson<QuestionnaireVersionInfo[]>(`/trainings/${trainingId}/versions`)
  },

  /**
   * The results as a file. Fetched as a blob and saved from a blob URL — the
   * TQS proxy is a different origin, and browsers ignore `download` on
   * cross-origin links.
   */
  async downloadResults(trainingId: string, format: 'md' | 'xlsx' | 'pdf', versionId?: string | null): Promise<void> {
    const params = new URLSearchParams({ format })
    if (versionId) params.set('version_id', versionId)
    const response = await scopedFetch(`${getTqsProxyBase()}/trainings/${trainingId}/results/export?${params}`, {
      headers: { Authorization: `Bearer ${await requireAccessToken()}` },
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }))
      throw new Error(apiErrorMessage(error, `Export failed (${response.status})`))
    }
    const disposition = response.headers.get('Content-Disposition') ?? ''
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition)
    const filename = match ? decodeURIComponent(match[1]) : `Auswertung.${format}`
    const url = URL.createObjectURL(await response.blob())
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 10_000)
  },

  async triggerAnalysis(
    trainingId: string,
  ): Promise<ActivityAnalysisCreateResponse> {
    return authedJson<ActivityAnalysisCreateResponse>(
      `/trainings/${trainingId}/analyze`,
      { method: 'POST' },
    )
  },

  /** Backend returns null (200 OK with `null` body) when no analysis row exists,
   *  so the AnalysisView can mount cleanly without a noisy 404 in DevTools. */
  async getLatestAnalysis(trainingId: string): Promise<ActivityAnalysis | null> {
    return authedJson<ActivityAnalysis | null>(`/trainings/${trainingId}/analysis`)
  },

  /** Poll for analysis completion. Returns when status is completed/failed. */
  async pollAnalysis(
    trainingId: string,
    options: {
      intervalMs?: number
      maxAttempts?: number
      signal?: AbortSignal
    } = {},
  ): Promise<ActivityAnalysis> {
    const intervalMs = options.intervalMs ?? 3000
    const maxAttempts = options.maxAttempts ?? 100
    for (let i = 0; i < maxAttempts; i++) {
      if (options.signal?.aborted) {
        throw new DOMException('Polling aborted', 'AbortError')
      }
      const analysis = await this.getLatestAnalysis(trainingId)
      // pollAnalysis is only called after triggerAnalysis returned a row, so
      // null here means the row was deleted — treat as a hard error.
      if (!analysis) {
        throw new Error('Analysis row disappeared during polling')
      }
      if (analysis.status === 'completed' || analysis.status === 'failed') {
        return analysis
      }
      // Abort-aware sleep — wakes up immediately on AbortSignal so the
      // caller doesn't have to wait up to `intervalMs` after unmount.
      await new Promise<void>((resolve, reject) => {
        const timeoutId = setTimeout(() => {
          options.signal?.removeEventListener('abort', onAbort)
          resolve()
        }, intervalMs)
        const onAbort = () => {
          clearTimeout(timeoutId)
          reject(new DOMException('Polling aborted', 'AbortError'))
        }
        if (options.signal?.aborted) {
          clearTimeout(timeoutId)
          reject(new DOMException('Polling aborted', 'AbortError'))
          return
        }
        options.signal?.addEventListener('abort', onAbort, { once: true })
      })
    }
    throw new Error('Analysis polling timed out')
  },

  // ==========================================================================
  // MARKDOWN IMPORT (#329)
  // ==========================================================================

  /**
   * Import markdown via the async job pattern:
   *   1. POST file — server enqueues a BackgroundTask and returns 202 + job id.
   *   2. Poll GET /import-markdown/{id} until status is terminal.
   *
   * The previous one-shot POST hit Cloudflare/AppGW 504s on large markdowns
   * because the LLM parse exceeded the gateway timeout. Splitting upload from
   * result keeps every HTTP hop short.
   */
  async importMarkdown(
    file: File,
    options: { intervalMs?: number; maxAttempts?: number; signal?: AbortSignal } = {},
  ): Promise<MarkdownImportResponse> {
    const token = await requireAccessToken()
    const formData = new FormData()
    formData.append('file', file)
    const enqueueResponse = await scopedFetch(`${getTqsProxyBase()}/trainings/import-markdown`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
      signal: options.signal,
    })
    if (!enqueueResponse.ok) {
      const error = await enqueueResponse.json().catch(() => ({ detail: enqueueResponse.statusText }))
      throw new Error(apiErrorMessage(error, `Markdown import failed (${enqueueResponse.status})`))
    }
    const created = (await enqueueResponse.json()) as MarkdownImportJobCreated

    const intervalMs = options.intervalMs ?? 3000
    const maxAttempts = options.maxAttempts ?? 100
    for (let i = 0; i < maxAttempts; i++) {
      if (options.signal?.aborted) {
        throw new DOMException('Polling aborted', 'AbortError')
      }
      const job = await authedJson<MarkdownImportJob>(
        `/trainings/import-markdown/${created.id}`,
        { signal: options.signal },
      )
      if (job.status === 'completed') {
        if (!job.result) {
          throw new Error('Import job completed without result')
        }
        return job.result
      }
      if (job.status === 'failed') {
        throw new Error(job.error_message || 'Markdown import failed')
      }
      await new Promise<void>((resolve, reject) => {
        const timeoutId = setTimeout(() => {
          options.signal?.removeEventListener('abort', onAbort)
          resolve()
        }, intervalMs)
        const onAbort = () => {
          clearTimeout(timeoutId)
          reject(new DOMException('Polling aborted', 'AbortError'))
        }
        if (options.signal?.aborted) {
          clearTimeout(timeoutId)
          reject(new DOMException('Polling aborted', 'AbortError'))
          return
        }
        options.signal?.addEventListener('abort', onAbort, { once: true })
      })
    }
    throw new Error('Markdown import polling timed out')
  },

  // ==========================================================================
  // PUBLIC ENDPOINTS (auth-free, used by /activity/:token route)
  // ==========================================================================

  async getPublicActivity(token: string): Promise<Activity> {
    const response = await scopedFetch(
      `${getTqsProxyBase()}/trainings/public/${token}`,
    )
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }))
      throw new Error(apiErrorMessage(error, 'Activity not available'))
    }
    return response.json()
  },

  async submitPublic(
    token: string,
    payload: ActivitySubmissionCreate,
  ): Promise<ActivitySubmission> {
    const response = await scopedFetch(
      `${getTqsProxyBase()}/trainings/public/${token}/submit`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      },
    )
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }))
      throw new Error(apiErrorMessage(error, `Submission failed (${response.status})`))
    }
    return response.json()
  },

  /** Pre-flight check: is this email allowed to submit to the public link?
   *  Returns the resolved name (from participants/users) on success, or
   *  throws with a reason ('not_assigned' | 'already_submitted' | other). */
  async verifyPublicEmail(
    token: string,
    email: string,
  ): Promise<{ ok: true; name: string | null }> {
    const response = await scopedFetch(
      `${getTqsProxyBase()}/trainings/public/${token}/verify-email`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      },
    )
    if (response.ok) {
      const data = await response.json()
      return { ok: true, name: data.name ?? null }
    }
    // Server returns { detail: { ok: false, reason: '...' } } on 403/409.
    const body = await response.json().catch(() => null)
    const reason =
      typeof body?.detail === 'object' && body.detail?.reason
        ? body.detail.reason
        : response.status === 409
          ? 'already_submitted'
          : 'not_assigned'
    const err = new Error(reason) as Error & { reason?: string }
    err.reason = reason
    throw err
  },
}
